import { useEffect, useState, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { CityMapPanel } from "@/components/analytics/CityMapPanel";

const RANGES = [
  { value: 7, label: "7D" },
  { value: 30, label: "30D" },
  { value: 90, label: "90D" },
  { value: 180, label: "180D" },
];

const SOURCE_LABELS = {
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  x: "X (Twitter)",
  google: "Google",
  youtube: "YouTube",
  reddit: "Reddit",
  snapchat: "Snapchat",
  direct: "Direct / Unknown",
  other: "Other",
};

const TOOLTIP_CONTENT_STYLE = { backgroundColor: "#121824", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 };

export function AnalyticsPanel({ pageId }) {
  const [days, setDays] = useState(30);
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/pages/${pageId}/analytics`, { params: { days } });
    setAnalytics(data);
  }, [pageId, days]);

  useEffect(() => {
    load();
  }, [load]);

  if (!analytics) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-6 w-6 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin" />
      </div>
    );
  }

  const ctr = analytics.total_views > 0 ? ((analytics.total_clicks / analytics.total_views) * 100).toFixed(1) : "0.0";

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      <div className="flex items-center gap-2" data-testid="analytics-range-selector">
        {RANGES.map((r) => (
          <button
            key={r.value}
            onClick={() => setDays(r.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${days === r.value ? "bg-indigo-500 text-white" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}
            data-testid={`analytics-range-${r.value}d`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-xs uppercase text-slate-400">Views</p>
          <p className="text-2xl font-black font-display pt-1" data-testid="analytics-total-views">{analytics.total_views}</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-xs uppercase text-slate-400">Clicks</p>
          <p className="text-2xl font-black font-display pt-1" data-testid="analytics-total-clicks">{analytics.total_clicks}</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-xs uppercase text-slate-400">CTR</p>
          <p className="text-2xl font-black font-display pt-1" data-testid="analytics-ctr">{ctr}%</p>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="analytics-trend-chart">
        <p className="text-sm font-semibold mb-3">Trend · Last {days} Days</p>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={analytics.trend}>
            <defs>
              <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6366F1" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="clicksGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10B981" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="date" stroke="#94A3B8" fontSize={10} tickLine={false} minTickGap={20} />
            <YAxis stroke="#94A3B8" fontSize={10} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} />
            <Area type="monotone" dataKey="views" stroke="#6366F1" fill="url(#viewsGrad)" strokeWidth={2} name="Views" />
            <Area type="monotone" dataKey="clicks" stroke="#10B981" fill="url(#clicksGrad)" strokeWidth={2} name="Clicks" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="analytics-per-link-breakdown">
          <p className="text-sm font-semibold mb-3">Clicks by Link</p>
          {analytics.per_link.length === 0 ? (
            <p className="text-sm text-slate-500">No clicks recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {analytics.per_link.map((l) => {
                const pct = analytics.total_clicks > 0 ? (l.clicks / analytics.total_clicks) * 100 : 0;
                return (
                  <div key={l.link_id} data-testid={`analytics-link-row-${l.link_id}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="capitalize">{l.label}</span>
                      <span className="text-slate-400">{l.clicks}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mt-1">
                      <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="analytics-source-breakdown">
          <p className="text-sm font-semibold mb-3">Traffic Source</p>
          {analytics.by_source.length === 0 ? (
            <p className="text-sm text-slate-500">No traffic recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {analytics.by_source.map((s) => {
                const max = Math.max(...analytics.by_source.map((x) => x.views + x.clicks), 1);
                const pct = ((s.views + s.clicks) / max) * 100;
                return (
                  <div key={s.source} data-testid={`analytics-source-row-${s.source}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span>{SOURCE_LABELS[s.source] || s.source}</span>
                      <span className="text-slate-400">{s.views} views · {s.clicks} clicks</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mt-1">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="analytics-city-breakdown">
        <p className="text-sm font-semibold mb-3">Top Cities</p>
        <CityMapPanel cities={analytics.by_city} />
        {analytics.by_city.length === 0 ? (
          <p className="text-sm text-slate-500 pt-3">No location data yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2 pt-3">
            {analytics.by_city.map((c) => (
              <div key={c.city} className="flex items-center justify-between text-sm rounded-lg bg-white/[0.03] px-3 py-2" data-testid={`analytics-city-row-${c.city}`}>
                <span className="flex items-center gap-2"><i className="fa-solid fa-location-dot text-indigo-400 text-xs" />{c.city}</span>
                <span className="text-slate-400">{c.views} views · {c.clicks} clicks</span>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-500 pt-3">City is detected from visitor IP on a best-effort basis; shows "Unknown" when it can't be resolved (e.g. private/local networks).</p>
      </div>
    </div>
  );
}
